/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { ContentLargeImageFragment } from "@cosmos/hygraph-schema";
import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";
import {
	Animate,
	BackgroundContainer,
	Caption,
	CosmosClickableBox,
	CosmosTagProps,
	ResponsiveContainer,
	SectionContainer,
	SectionContainerProps,
	SectionHeading,
	Wrap,
} from "@components/elements";
import { cx } from "@panda/css";
import { Box, Flex, VStack } from "@panda/jsx";

import { ContentLargeImageMainStack } from "./ContentLargeImage.MainStack";
import { ContentLargeImageMedia } from "./ContentLargeImage.Media";
import { useLocale, useLocales } from "@epic-mw/i18n";
import { normalizeLocaleHref } from "@cosmos/common-utils";
import { useCosmosComponentContext, defaultGetLocalizedPath } from "@components/context";
import {
	animateCustomCss,
	assetWrapStyles,
	hoverLinkStyles,
	sectionFlexStyles,
} from "./ContentLargeImage.section.styles";

export interface ContentLargeImageSectionProps extends Omit<ContentLargeImageFragment, "badges"> {
	className?: string;
	sectionIndex?: number;
	/** True when a page hero renders above this section — then even the first section is an h2, not the page h1. */
	hasHero?: boolean;
	enableAnimate?: boolean;
	badges?: CosmosTagProps[] | null;
	date?: string | null;
	verticallyCenter?: boolean | null;
	sectionContainerProps?: SectionContainerProps;
}

export const ContentLargeImageSection = (props: ContentLargeImageSectionProps | null): React.JSX.Element | null => {
	const locale = useLocale();
	const locales = useLocales();
	const { getLocalizedPath: contextGetLocalizedPath, localePrefix } = useCosmosComponentContext();
	const getLocalizedPath = contextGetLocalizedPath ?? defaultGetLocalizedPath;

	const {
		config,
		eyebrow,
		title,
		desc,
		buttons: buttonsProp,
		modButtons,
		badges,
		date,
		asset,
		caption,
		blurredImage,
		flip,
		gradient,
		verticallyCenter,
		className,
		sectionIndex,
		hasHero,
		enableAnimate: enableAnimateProp,
		sectionContainerProps,
	} = props || {};

	const enableAnimate = enableAnimateProp !== undefined ? enableAnimateProp : true;
	const isFirstSection = sectionIndex === 0;
	// The page h1 belongs to the hero. This section only owns the h1 when it's the first
	// section AND no hero renders above it; otherwise its title nests as an h2/h3.
	const isPageTitle = isFirstSection && !hasHero;
	const validSectionTitle = Boolean(config?.heading?.title?.text?.trim());
	const mainStackTitleAs = isPageTitle ? (validSectionTitle ? "h2" : "h1") : validSectionTitle ? "h3" : "h2";

	const buttons = (modButtons?.length ? modButtons : buttonsProp) || [];
	let href: string | null = null;
	if (buttons.length === 1 && buttons[0].href) {
		href = normalizeLocaleHref(buttons[0].href, locale, locales, getLocalizedPath, localePrefix);
	}
	const isClickableBox = Boolean(href);

	return (
		<BackgroundContainer {...extractBackgroundContainerProps(config)}>
			<ResponsiveContainer>
				<SectionContainer
					contentVisibility="unset"
					{...sectionContainerProps}
					{...extractSectionContainerProps(config)}
				>
					<VStack alignItems="stretch" gap="eds.dimension.56" className={cx("content-large-image-section", className)}>
						<Wrap
							key={config?.heading?.title?.text || ""}
							condition={enableAnimate && validSectionTitle ? isFirstSection : false}
							wrap={(children) => <Animate type="slide">{children}</Animate>}
						>
							<SectionHeading
								{...config?.heading}
								title={{ ...config?.heading?.title, as: isPageTitle ? "h1" : "h2" }}
							/>
						</Wrap>
						<Box position="relative">
							{isClickableBox ? (
								<CosmosClickableBox.Link
									href={href || ""}
									aria-label={title?.text}
									className={hoverLinkStyles}
									analytics={{
										eventCategory: "tile",
										eventLabel: title?.text || "",
										eventValue: {
											component: "ContentLargeImage",
											href,
										},
									}}
								/>
							) : null}

							<Flex
								direction={{ base: flip ? "row-reverse" : "row", mdDown: "column" }}
								justifyContent="space-between"
								alignItems={verticallyCenter ? "center" : undefined}
								columnGap="eds.dimension.64"
								rowGap={{ md: "eds.dimension.80", mdDown: "eds.dimension.48" }}
								className={cx("section-flex", sectionFlexStyles)}
							>
								<ContentLargeImageMainStack
									animate={enableAnimate ? isFirstSection : false}
									eyebrow={eyebrow || ""}
									gradient={gradient}
									title={title}
									titleAs={mainStackTitleAs}
									desc={desc}
									buttons={buttons}
									date={date}
								/>

								{asset?.url ? (
									<Box className={cx("asset-wrap", assetWrapStyles)}>
										<Wrap
											key={asset.url}
											condition={enableAnimate ? isFirstSection : false}
											wrap={(children) => (
												<Animate delay={0.5} type="slideLeft" customCss={animateCustomCss}>
													{children}
												</Animate>
											)}
										>
											<Caption caption={caption}>
												<ContentLargeImageMedia asset={asset} blurredImage={blurredImage} tags={badges || []} />
											</Caption>
										</Wrap>
									</Box>
								) : null}
							</Flex>
						</Box>
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
